import {
  CFormInput,
  CFormLabel,
  CButton,
  CImage,
  CFormTextarea,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faEdit,
  faEye,
  faEyeLowVision,
} from "@fortawesome/free-solid-svg-icons";
import { useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Select from "react-select";
import { imagePreviewFromik } from "@/helpers/common";
import { toast } from "react-toastify";
import constants from "@/libs/constant";
import { useTranslationContext } from "@/context/TranslationContext";

/**
 * CustomInput Component
 *
 * A versatile input component supporting various input types like text, password, email, phone, file, and select dropdowns.
 * - Toggles password visibility for password input.
 * - Shows an edit icon for email fields.
 * - Displays a file preview and allows removal for file input.
 * - Handles validation errors and customization.
 * - Supports `react-select` for dropdowns and `react-phone-number-input` for phone input.
 *
 * Props:
 * - `label` (string): Input label.
 * - `type` (string): Input type ("text", "password", "email", "select", "phone", "file").
 * - `options` (array): Used for `type="select"`. Format: [{ value, label }].
 * - `value`, `name`, `placeholder`: Standard input attributes.
 * - `onChange`, `onBlur`, `onEditClick`: Event handlers.
 * - `error` (string): Displays validation errors.
 * - `disabled` (boolean): Disables input.
 * - `accept` (string): File type restrictions for file input.
 * - `className` (string): Custom CSS class for styling.
 * - `...props`: Additional props passed to the input component.
 */
const CustomInput = ({
  label,
  type = "text",
  name,
  placeholder,
  classNameHeader,
  className = "form-control same-input-cls",
  classNamePrefix,
  value,
  error,
  onEditClick,
  disabled = false,
  onChange,
  accept, // New prop for file type restrictions
  options = [],
  ...props
}) => {
  const t = useTranslationContext();
  const [showPassword, setShowPassword] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const fileInputRef = useRef(null);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    // Custom validation for file types
    if (file) {
      if (!constants.validation.settings.supportedImageFormats.includes(file.type)) {
        toast.error(t('admin.file_upload_invalid_format'));
        // Clear the file input
        event.target.value = '';
        return;
      }

      onChange(file); // Pass the file to the parent form
      const previewUrl = URL.createObjectURL(file);
      setFilePreview(previewUrl);
    }
  };
  const handleFileRemove = () => {
    onChange(null);
    setFilePreview(null);
  };

  return (
    <div className={`custom-input ${classNameHeader || "mb-3"}`}>
      {label && <CFormLabel>{label}</CFormLabel>}
      {type === "file" ? (
        <>
          <CFormInput
            {...props}
            type="file"
            hidden
            ref={fileInputRef}
            name={name}
            onChange={handleFileChange}
            accept={accept}
          />
          <CButton
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-1 upload-button"
          >
            Upload File
          </CButton>
          {(filePreview || value) && (
            <div className="preview-image mt-2">
              <CImage src={imagePreviewFromik(value)} alt="preview" />
              <FontAwesomeIcon
                icon={faClose}
                onClick={handleFileRemove}
                className="close-icon"
              />
            </div>
          )}
        </>
      ) : type === "select" ? (
        <Select
          {...props}
          options={options}
          isDisabled={disabled}
          name={name}
          placeholder={placeholder}
          classNamePrefix={`${classNamePrefix ? classNamePrefix : "react-select-prefix"
            }`}
          value={value}
          onChange={onChange}
        />
      ) : type === "textarea" ? (
        <CFormTextarea
          {...props}
          disabled={disabled}
          name={name}
          placeholder={placeholder}
          className={className}
          value={value}
          onChange={onChange}
        />
      ) : (
        <div className="input-group">
          {type === "phone" ? (
            <div className="input-group">
              <span className="input-group-text">
                🇩🇪
              </span>
              <CFormInput
                {...props}
                type="tel"
                name={name}
                value={value || '+49 '}
                placeholder="+49 "
                className={className}
                disabled={disabled}
                onChange={(e) => {
                  let input = e.target.value || '';

                  // Remove everything except digits
                  let digits = input.replace(/\D/g, '');

                  // Remove leading 49 if user types it manually
                  if (digits.startsWith('49')) {
                    digits = digits.slice(2);
                  }

                  // Limit digits (13 max after +49)
                  digits = digits.slice(0, 13);

                  const formatted = `+49   ${digits}`;

                  onChange(formatted);
                }}
                placeholder="+49"
                maxLength={17}
              />
            </div>
          ) : (
            <CFormInput
              {...props}
              type={
                type === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : type
              }
              disabled={disabled}
              name={name}
              placeholder={placeholder}
              className={className}
              value={value}
              onChange={onChange}
            />
          )}
          {type === "password" && (
            <span
              className="input-group-text"
              onClick={togglePasswordVisibility}
            >
              <FontAwesomeIcon icon={showPassword ? faEye : faEyeLowVision} />
            </span>
          )}
          {type === "email" && disabled && (
            <span className="input-group-text" onClick={onEditClick}>
              <FontAwesomeIcon icon={faEdit} />
            </span>
          )}
          {type === "percentage" && (
            <span className="input-group-text" onClick={onEditClick}>
              {"%"}
            </span>
          )}
        </div>
      )}
      {error && <div className="error text-danger mt-1">{error}</div>}
    </div>
  );
};

export default CustomInput;
